import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MilestoneCard from './MilestoneCard';
import { MILESTONE_ICONS, SPECIAL_MILESTONES, PET_MILESTONES } from '../data/loveData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const MilestonesDisplay = ({ milestones = PET_MILESTONES, calculatedDays, anniversaryDate, language }) => {
  const [showAllMilestones, setShowAllMilestones] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Define filter options - Always visible regardless of date selection
  const filterOptions = [
    { id: 'all', labelZh: '全部', labelEn: 'All', icon: FiIcons.FiGrid },
    { id: 'achieved', labelZh: '已達成', labelEn: 'Achieved', icon: FiIcons.FiCheckCircle },
    { id: 'upcoming', labelZh: '即將到來', labelEn: 'Upcoming', icon: FiIcons.FiClock },
    { id: 'special', labelZh: '特殊紀念', labelEn: 'Special', icon: FiIcons.FiStar }
  ];
  
  // Check if we have valid days calculation
  const hasValidDays = typeof calculatedDays === 'number' && !isNaN(calculatedDays) && calculatedDays >= 0;
  
  // Determine which milestones to show
  const pastMilestones = hasValidDays
    ? milestones.filter(days => days <= calculatedDays)
    : [];
  
  const upcomingMilestones = hasValidDays
    ? milestones.filter(days => days > calculatedDays)
    : milestones; // Show all milestones as upcoming if no date selected
    
  const specialMilestones = milestones.filter(days => SPECIAL_MILESTONES.includes(days));
  
  // Apply filters
  let filteredPastMilestones = pastMilestones;
  let filteredUpcomingMilestones = upcomingMilestones;
  
  if (activeFilter === 'achieved') {
    filteredUpcomingMilestones = [];
  } else if (activeFilter === 'upcoming') {
    filteredPastMilestones = [];
  } else if (activeFilter === 'special') {
    filteredPastMilestones = pastMilestones.filter(days => SPECIAL_MILESTONES.includes(days));
    filteredUpcomingMilestones = upcomingMilestones.filter(days => SPECIAL_MILESTONES.includes(days));
  }
  
  // Sort past milestones from recent to distant (highest day count to lowest)
  filteredPastMilestones = [...filteredPastMilestones].sort((a, b) => b - a);
  
  // Limit the number of milestones if not showing all
  if (!showAllMilestones) {
    filteredPastMilestones = filteredPastMilestones.slice(0, 5);
    filteredUpcomingMilestones = filteredUpcomingMilestones.slice(0, 5);
  }
  
  // Get milestone icon
  const getMilestoneIcon = (days) => {
    return MILESTONE_ICONS[days] || MILESTONE_ICONS.default;
  };
  
  // Check if milestone is special
  const isSpecialMilestone = (days) => {
    return SPECIAL_MILESTONES.includes(days);
  };
  
  // Check if any day is today
  const isToday = (days) => {
    return days === calculatedDays;
  };
  
  // Container variants for staggered animation
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  // Item variants for animation
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div>
      {/* Milestones Summary - Only show if date is selected and milestones achieved */}
      {hasValidDays && pastMilestones.length > 0 && (
        <div className="mb-6 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-love-100 dark:bg-love-900/30 text-love-600 dark:text-love-300 shadow-sm">
            <span className="text-lg mr-2">🎉</span>
            <span className="font-medium">
              {language === 'zh' 
                ? `已達成 ${pastMilestones.length} 個里程碑！` 
                : `Reached ${pastMilestones.length} milestones!`}
            </span>
          </div>
        </div>
      )}

      {/* Filter Tabs - Always show regardless of date selection */}
      <div className="flex items-center justify-center mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg overflow-hidden overflow-x-auto">
        {filterOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setActiveFilter(option.id)}
            className={`flex items-center px-3 py-2 text-sm rounded-md transition-all ${
              activeFilter === option.id
                ? 'bg-white dark:bg-gray-700 text-love-600 dark:text-love-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            <SafeIcon icon={option.icon} className="mr-1" />
            <span>{language === 'zh' ? option.labelZh : option.labelEn}</span>
            {option.id === 'achieved' && pastMilestones.length > 0 && (
              <span className="ml-1 text-xs bg-love-100 dark:bg-love-900 text-love-600 dark:text-love-300 px-1.5 py-0.5 rounded-full">
                {pastMilestones.length}
              </span>
            )}
            {option.id === 'special' && (
              <span className="ml-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                {specialMilestones.length}
              </span>
            )}
          </button>
        ))}
      </div>
      
      <AnimatePresence mode="wait">
        <motion.div 
          key={activeFilter}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {/* Past Milestones - Only show if we have valid days calculation */}
          {filteredPastMilestones.length > 0 && (
            <>
              <h4 className="font-medium text-gray-800 dark:text-gray-300 mb-3 flex items-center">
                <span className="emoji mr-2">🏆</span>
                {language === 'zh' ? '已達成的里程碑' : 'Reached Milestones'}
              </h4>
              
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                variants={container}
                initial="hidden"
                animate="show"
              >
                {filteredPastMilestones.map((days) => (
                  <motion.div key={`past-${days}`} variants={item}>
                    <MilestoneCard 
                      days={days}
                      date={anniversaryDate}
                      icon={getMilestoneIcon(days)}
                      isSpecial={isSpecialMilestone(days)}
                      language={language}
                      isPast={true}
                      isToday={isToday(days)}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}
          
          {/* Upcoming Milestones - Always show even without date */}
          {filteredUpcomingMilestones.length > 0 && (
            <>
              <h4 className="font-medium text-gray-800 dark:text-gray-300 mb-3 flex items-center">
                <span className="emoji mr-2">🎊</span>
                {language === 'zh' ? '即將到來的里程碑' : 'Upcoming Milestones'}
              </h4>
              
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                variants={container}
                initial="hidden"
                animate="show"
              >
                {filteredUpcomingMilestones.map((days) => (
                  <motion.div key={`upcoming-${days}`} variants={item}>
                    <MilestoneCard 
                      days={days}
                      date={anniversaryDate}
                      icon={getMilestoneIcon(days)}
                      isSpecial={isSpecialMilestone(days)}
                      language={language}
                      isUpcoming={true}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}
          
          {/* No Results */}
          {filteredPastMilestones.length === 0 && filteredUpcomingMilestones.length === 0 && (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">
                {language === 'zh' ? '沒有符合此篩選條件的里程碑' : 'No milestones match this filter'}
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      
      {/* Show More/Less Button */}
      <AnimatePresence>
        {(pastMilestones.length > 5 || upcomingMilestones.length > 5) && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            onClick={() => setShowAllMilestones(!showAllMilestones)}
            className="mt-6 w-full flex items-center justify-center py-2 px-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <span>{showAllMilestones ? (language === 'zh' ? '顯示較少' : 'Show Less') : (language === 'zh' ? '顯示全部' : 'Show All')}</span>
            <SafeIcon 
              icon={FiIcons.FiChevronDown} 
              className={`ml-1 transition-transform duration-300 ${showAllMilestones ? 'rotate-180' : ''}`} 
            />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MilestonesDisplay;